import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { isEqual } from 'lodash'
import { useToaster } from '@/modules/toaster/composables/use-toaster'
import { useAppStore } from '@/modules/app/stores/app.store'
import { useUserStore } from '@/modules/users/stores/user.store'
import { useFilterStore } from '@/modules/filter/stores/filter.store'
import { MotionTrackDefault, type MotionTrack } from '@/shared/interfaces/item'
import { motionViewApi } from '../api/motion.api'
import { MotionBoxType, MotionStatusErrorCode } from '../types/motion.enums'
import type { MBox, MBoxPriorityItem, OutboundShipmentsMonitorByIds } from '@/api/models/types'
import type { HttpResponse } from '@/modules/services/http'
import type { Shipment, Validation } from '@/shared/models/shared.model'
import type {
  EncryptedKeyValue,
  ExternalKey,
  Attachment,
  MotionViewStatus,
  ShipmentDate,
  Reference,
  GeneralInfo,
  CurrentBox,
  SendTo,
  GeneralHistory,
  Carrier,
  CarrierHistory,
  CarrierSetting,
  SendFrom,
  Dimension,
  DimensionHistory,
  Recipient,
  Payer,
  AdditionalInfo,
  OrderInfo,
  OrderDelivery,
  OrderDeliveryHistory,
  ShipmentInfo,
  ShipmentDelivery,
  ShipmentDeliveryHistory,
  ShipmentSpecification,
} from '../models/motion.model'
import type { MotionBoxErrorCode } from '@/modules/motion-box/types/motion-box.enums'
import type { MotionBoxStatusRequest, NotificationRequest } from '../api/motion.requests'

export const useMotionStore = defineStore('motion', () => {
  const appStore = useAppStore()
  const userStore = useUserStore()
  const filterStore = useFilterStore()

  const { success, warning } = useToaster()

  const currentBox = ref<CurrentBox>({
    index: -1,
    mBoxId: null,
    mType: MotionBoxType.None,
  })

  const statuses = ref<MotionViewStatus[]>([])
  const lastKnownChangeVersion = ref<string>('0')
  const trackId = ref<number>(0)
  const items = ref<MBox[]>([])
  const priorityItems = ref<MBoxPriorityItem[]>([])
  const generalInfo = ref<Record<number, GeneralInfo>>({})
  const generalHistory = ref<Record<number, GeneralHistory>>({})
  const additionalInfo = ref<Record<number, AdditionalInfo>>({})
  const dimensions = ref<Record<number, Dimension>>({})
  const dimensionHistory = ref<Record<number, DimensionHistory>>({})
  const sendTo = ref<Record<number, SendTo>>({})
  const sendFrom = ref<Record<number, SendFrom>>({})
  const carrier = ref<Record<number, Carrier>>({})
  const carrierHistory = ref<Record<number, CarrierHistory>>({})
  const carrierSetting = ref<Record<number, CarrierSetting>>({})
  const orderInfo = ref<Record<number, OrderInfo>>({})
  const orderDelivery = ref<Record<number, OrderDelivery>>({})
  const orderDeliveryHistory = ref<Record<number, OrderDeliveryHistory>>({})
  const shipmentInfo = ref<Record<number, ShipmentInfo>>({})
  const shipmentDelivery = ref<Record<number, ShipmentDelivery>>({})
  const shipmentDeliveryHistory = ref<Record<number, ShipmentDeliveryHistory>>({})
  const shipmentSpecification = ref<Record<number, ShipmentSpecification>>({})
  const recipient = ref<Record<number, Recipient>>({})
  const payer = ref<Record<number, Payer>>({})

  const isLoading = ref({
    update: false,
    init: false,
  })

  const currentIndex = computed((): number => currentBox.value.index)

  const currentMotionBoxId = computed((): number | null => currentBox.value.mBoxId)

  const currentMotionBoxType = computed((): number | null => currentBox.value.mType)

  const currentGeneralInfo = computed((): GeneralInfo | null => {
    if (!currentMotionBoxId.value) return null

    return generalInfo.value[currentMotionBoxId.value] ?? null
  })

  const currentGeneralHistory = computed((): GeneralHistory | null => {
    if (!currentMotionBoxId.value) return null

    return generalHistory.value[currentMotionBoxId.value] ?? null
  })

  const currentAdditionalInfo = computed((): AdditionalInfo | null => {
    if (!currentMotionBoxId.value) return null

    return additionalInfo.value[currentMotionBoxId.value] ?? null
  })

  const currentDimension = computed((): Dimension | null => {
    if (!currentMotionBoxId.value) return null

    return dimensions.value[currentMotionBoxId.value] ?? null
  })

  const currentDimensionHistory = computed((): DimensionHistory | null => {
    if (!currentMotionBoxId.value) return null

    return dimensionHistory.value[currentMotionBoxId.value] ?? null
  })

  const currentSendTo = computed((): SendTo | null => {
    if (!currentMotionBoxId.value) return null

    return sendTo.value[currentMotionBoxId.value] ?? null
  })

  const currentSendFrom = computed((): SendTo | null => {
    if (!currentMotionBoxId.value) return null

    return sendFrom.value[currentMotionBoxId.value] ?? null
  })

  const currentCarrier = computed((): Carrier | null => {
    if (!currentMotionBoxId.value) return null

    return carrier.value[currentMotionBoxId.value] ?? null
  })

  const currentCarrierHistory = computed((): CarrierHistory | null => {
    if (!currentMotionBoxId.value) return null

    return carrierHistory.value[currentMotionBoxId.value] ?? null
  })

  const currentCarrierSetting = computed((): CarrierSetting | null => {
    if (!currentMotionBoxId.value) return null

    return carrierSetting.value[currentMotionBoxId.value] ?? null
  })

  const currentOrderInfo = computed((): OrderInfo | null => {
    if (!currentMotionBoxId.value) return null

    return orderInfo.value[currentMotionBoxId.value] ?? null
  })

  const currentOrderDelivery = computed((): OrderDelivery | null => {
    if (!currentMotionBoxId.value) return null

    return orderDelivery.value[currentMotionBoxId.value] ?? null
  })

  const currentOrderDeliveryHistory = computed((): OrderDeliveryHistory | null => {
    if (!currentMotionBoxId.value) return null

    return orderDeliveryHistory.value[currentMotionBoxId.value] ?? null
  })

  const currentShipmentInfo = computed((): ShipmentInfo | null => {
    if (!currentMotionBoxId.value) return null

    return shipmentInfo.value[currentMotionBoxId.value] ?? null
  })

  const currentShipmentDelivery = computed((): ShipmentDelivery | null => {
    if (!currentMotionBoxId.value) return null

    return shipmentDelivery.value[currentMotionBoxId.value] ?? null
  })

  const currentShipmentDeliveryHistory = computed((): ShipmentDeliveryHistory | null => {
    if (!currentMotionBoxId.value) return null

    return shipmentDeliveryHistory.value[currentMotionBoxId.value] ?? null
  })

  const currentShipmentSpecification = computed((): ShipmentSpecification | null => {
    if (!currentMotionBoxId.value) return null

    return shipmentSpecification.value[currentMotionBoxId.value] ?? null
  })

  const currentRecipient = computed((): Recipient | null => {
    if (!currentMotionBoxId.value) return null

    return recipient.value[currentMotionBoxId.value] ?? null
  })

  const currentPayer = computed((): Recipient | null => {
    if (!currentMotionBoxId.value) return null

    return payer.value[currentMotionBoxId.value] ?? null
  })

  const hasGeneralInfo = (mBoxId: number): boolean => mBoxId in generalInfo.value

  const hasGeneralHistory = (mBoxId: number): boolean => mBoxId in generalHistory.value

  const hasAdditionalInfo = (mBoxId: number): boolean => mBoxId in additionalInfo.value

  const hasDimensions = (mBoxId: number): boolean => mBoxId in dimensions.value

  const hasDimensionHistory = (mBoxId: number): boolean => mBoxId in dimensionHistory.value

  const hasSendTo = (mBoxId: number): boolean => mBoxId in sendTo.value

  const hasSendFrom = (mBoxId: number): boolean => mBoxId in sendFrom.value

  const hasCarrier = (mBoxId: number): boolean => mBoxId in carrier.value

  const hasCarrierHistory = (mBoxId: number): boolean => mBoxId in carrierHistory.value

  const hasCarrierSetting = (mBoxId: number): boolean => mBoxId in carrierSetting.value

  const hasOrderInfo = (mBoxId: number): boolean => mBoxId in orderInfo.value

  const hasOrderDelivery = (mBoxId: number): boolean => mBoxId in orderDelivery.value

  const hasOrderDeliveryHistory = (mBoxId: number): boolean => mBoxId in orderDeliveryHistory.value

  const hasShipmentInfo = (mBoxId: number): boolean => mBoxId in shipmentInfo.value

  const hasShipmentDelivery = (mBoxId: number): boolean => mBoxId in shipmentDelivery.value

  const hasShipmentDeliveryHistory = (mBoxId: number): boolean =>
    mBoxId in shipmentDeliveryHistory.value

  const hasShipmentSpecification = (mBoxId: number): boolean =>
    mBoxId in shipmentSpecification.value

  const hasRecipient = (mBoxId: number): boolean => mBoxId in recipient.value

  const hasPayer = (mBoxId: number): boolean => mBoxId in payer.value

  const setCurrentBox = (index: number, mBoxId: number, mType: MotionBoxType): void => {
    currentBox.value = {
      index,
      mBoxId,
      mType,
    }
  }

  const clearCurrentBox = (): void => {
    currentBox.value = {
      index: -1,
      mBoxId: null,
      mType: MotionBoxType.None,
    }
  }

  const loadGeneralInfo = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getGeneralInfo(mBoxId)

    if (response.error || !response.data) {
      generalInfo.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    generalInfo.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadGeneralHistory = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getGeneralHistory(mBoxId)

    if (response.error || !response.data) {
      generalHistory.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    generalHistory.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadAdditionalInfo = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getAdditionalInfo(mBoxId)

    if (response.error || !response.data) {
      additionalInfo.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    additionalInfo.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadDimension = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getDimension(mBoxId)

    if (response.error || !response.data) {
      dimensions.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    dimensions.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadDimensionHistory = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getDimensionHistory(mBoxId)

    if (response.error || !response.data) {
      dimensionHistory.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    dimensionHistory.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadSendTo = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getSendTo(mBoxId)

    if (response.error || !response.data) {
      sendTo.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    sendTo.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadSendFrom = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getSendFrom(mBoxId)

    if (response.error || !response.data) {
      sendFrom.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    sendFrom.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadCarrier = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getCarrier(mBoxId)

    if (response.error || !response.data) {
      carrier.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    carrier.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadCarrierHistory = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getCarrierHistory(mBoxId)

    if (response.error || !response.data) {
      carrierHistory.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    carrierHistory.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadCarrierSetting = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getCarrierSetting(mBoxId)

    if (response.error || !response.data) {
      carrierSetting.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    carrierSetting.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadOrderInfo = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getOrderInfo(mBoxId)

    if (response.error || !response.data) {
      orderInfo.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    orderInfo.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadOrderDelivery = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getOrderDelivery(mBoxId)

    if (response.error || !response.data) {
      orderDelivery.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    orderDelivery.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadOrderDeliveryHistory = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getOrderDeliveryHistory(mBoxId)

    if (response.error || !response.data) {
      orderDeliveryHistory.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    orderDeliveryHistory.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadShipmentInfo = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getShipmentInfo(mBoxId)

    if (response.error || !response.data) {
      shipmentInfo.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    shipmentInfo.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadShipmentDelivery = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getShipmentDelivery(mBoxId)

    if (response.error || !response.data) {
      shipmentDelivery.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    shipmentDelivery.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadShipmentDeliveryHistory = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getShipmentDeliveryHistory(mBoxId)

    if (response.error || !response.data) {
      shipmentDeliveryHistory.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    shipmentDeliveryHistory.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadShipmentSpecification = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getShipmentSpecification(mBoxId)

    if (response.error || !response.data) {
      shipmentSpecification.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    shipmentSpecification.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadRecipient = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getRecipient(mBoxId)

    if (response.error || !response.data) {
      recipient.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    recipient.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadPayer = async (mBoxId: number): Promise<boolean> => {
    const response = await motionViewApi.getPayer(mBoxId)

    if (response.error || !response.data) {
      payer.value[mBoxId] = {
        mBoxId: mBoxId,
        hasError: true,
      }
      return false
    }

    payer.value[response.data.mBoxId] = { ...response.data }
    return true
  }

  const loadStatuses = async (): Promise<void> => {
    const { data, error } = await motionViewApi.getStatuses()

    if (!error && data) {
      statuses.value = data
    }
  }

  const getDimensionValueIds = async (
    mBoxId: number,
  ): Promise<HttpResponse<Record<number, number>, MotionBoxErrorCode>> =>
    await motionViewApi.getDimensionValueIds(mBoxId)

  const getShipmentLabelByRelatedId = (relatedId?: number): string | undefined => {
    const item = items.value.find((item) => item.id === relatedId)

    if (!item) return undefined

    return item.label
  }

  const getShipmentDate = async (
    mBoxId: number,
  ): Promise<HttpResponse<ShipmentDate, MotionBoxErrorCode>> => {
    return await motionViewApi.getShipmentDate(mBoxId)
  }

  const getReference = async (
    mBoxId: number,
  ): Promise<HttpResponse<Reference, MotionBoxErrorCode>> => {
    return await motionViewApi.getReference(mBoxId)
  }

  const getStatusValidation = async (
    motionBoxIds: number[],
  ): Promise<HttpResponse<Validation | null, MotionBoxErrorCode>> => {
    return motionViewApi.getStatusValidation(motionBoxIds)
  }

  const getBoxes = async (): Promise<void> => {
    items.value = []

    const { data } = await motionViewApi.getBoxes(filterStore.filter)

    if (data && data.items && isEqual(data.status, filterStore.filter.status)) {
      items.value = data.items
      lastKnownChangeVersion.value = data.changeVersion
      trackId.value = data.trackId

      if (data.items.length >= 10000) {
        warning('message.maxLimitReached', { count: data.items.length })
      } else {
        success('glossary.showingCount', { count: data.items.length })
      }
    }
  }

  const getBoxesByShipmentNo = async (shipmentNo: string): Promise<void> => {
    items.value = []

    const { error, data } = await motionViewApi.getBoxesByShipmentNo(shipmentNo)

    if (error || !data) return

    items.value = data.items

    const clientKey = data.items[0]?.clientKey

    if (clientKey) {
      userStore.setActiveClientByKey(clientKey)
    }

    success('glossary.showingCount', { count: data.items.length })
  }

  const getRelatedMotionBoxes = async (): Promise<void> => {
    const relatedId = filterStore.tagContext?.relatedId

    if (relatedId) {
      const item = items.value.find((item) => item.id === relatedId)
      const { data } = await motionViewApi.getRelatedMotionBox(relatedId)
      if (data) {
        items.value = data.priorityItems
      } else if (item) {
        items.value = [{ ...item }]
      }
    }
  }

  const refreshMotionItems = async (): Promise<void> => {
    appStore.isIndeterminate = true
    isLoading.value.init = true

    const tagContext = filterStore.tagContext

    if (!tagContext) {
      await getBoxes()
    } else {
      if (tagContext.relatedId) {
        await getRelatedMotionBoxes()
      } else if (tagContext.shipmentNo) {
        await getBoxesByShipmentNo(tagContext.shipmentNo)
      }
    }

    isLoading.value.init = false
    appStore.isIndeterminate = false
  }

  const getExternalKey = async (
    mBoxId: number,
  ): Promise<HttpResponse<ExternalKey | null, MotionBoxErrorCode>> => {
    return await motionViewApi.getExternalKey(mBoxId)
  }

  const getAdminExternalKey = async (
    mBoxId: number,
  ): Promise<HttpResponse<ExternalKey | null, MotionBoxErrorCode>> => {
    return await motionViewApi.getAdminExternalKey(mBoxId)
  }

  const getDeliveryNote = async (
    mBoxId: number,
  ): Promise<HttpResponse<EncryptedKeyValue | null, MotionBoxErrorCode>> => {
    return await motionViewApi.getDeliveryNote(mBoxId)
  }

  const getAdminDeliveryNote = async (
    mBoxId: number,
  ): Promise<HttpResponse<EncryptedKeyValue | null, MotionBoxErrorCode>> => {
    return await motionViewApi.getAdminDeliveryNote(mBoxId)
  }

  const getMotionSelectedItems = async (): Promise<void> => {
    appStore.isIndeterminate = true
    isLoading.value.init = true

    items.value = []

    const { data } = await motionViewApi.searchBoxes({ priorityItems: priorityItems.value })

    if (data && data.priorityItems) {
      items.value = data.priorityItems
      lastKnownChangeVersion.value = data.changeVersion
      trackId.value = data.trackId
    }

    isLoading.value.init = false
    appStore.isIndeterminate = false
  }

  const firstItem = (): MotionTrack | null => {
    if (items.value.length >= 1) {
      return {
        id: items.value[0].id,
        index: 0,
        status: items.value[0].status,
        mType: getMotionBoxType(items.value[0].mType),
      }
    }
    return null
  }

  const getUpdatedStatus = (updatedItems: MBox[]): { [key: number]: MBox } => {
    return updatedItems.reduce((updatedState: { [key: number]: MBox }, updateItem: MBox) => {
      const index = items.value.findIndex((item: MBox) => item.id === updateItem.id)
      if (index > -1) {
        updatedState[index] = updateItem
      }
      return updatedState
    }, {})
  }

  const updateMotionItems = async (): Promise<{ success: boolean; error?: string }> => {
    const payload: OutboundShipmentsMonitorByIds = {
      fromViewDate: filterStore.filter.fromViewDate,
      toViewDate: filterStore.filter.toViewDate,
      status: filterStore.filter.status,
      fallbackQueuePriority: 15,
      priorityItems: priorityItems.value,
    }

    const { data, error } = await motionViewApi.getUpdatedBoxes(payload)
    if (error) return { success: false, error }

    if (data) {
      const updatedStates = getUpdatedStatus(data.priorityItems)
      for (const [key, value] of Object.entries(updatedStates)) {
        items.value[Number(key)].status = value.status
        items.value[Number(key)].flag = value.flag
        items.value[Number(key)].severity = value.severity
        items.value[Number(key)].mType = value.mType
        items.value[Number(key)].isHidden = value.isHidden
      }
    }
    return { success: true }
  }

  const updateSingle = async (mBoxId: number): Promise<void> => {
    const { data, error } = await motionViewApi.getSelectedBox(mBoxId)
    if (error) {
      //toastStore.showToast(ERROR, error)
    } else if (data) {
      const index = items.value.findIndex((item: MBox) => item.id === mBoxId)
      if (index > -1) {
        items.value[index].status = data.status
        items.value[index].flag = data.flag
        items.value[index].severity = data.severity
        items.value[index].isHidden = data.isHidden
      }
    }
  }

  const updateStatus = async (
    mBoxId: number,
    status: MotionBoxStatusRequest,
  ): Promise<HttpResponse<Shipment | null, MotionStatusErrorCode>> => {
    return await motionViewApi.updateStatus(mBoxId, status)
  }

  const updateDimensions = async (
    mBoxId: number,
    dimensions: Record<number, number>,
  ): Promise<HttpResponse<Shipment | null, MotionBoxErrorCode>> => {
    return await motionViewApi.updateDimensions(mBoxId, dimensions)
  }

  const updateShipmentDate = async (
    mBoxId: number,
    request: ShipmentDate,
  ): Promise<HttpResponse<Shipment | null, MotionBoxErrorCode>> => {
    return await motionViewApi.updateShipmentDate(mBoxId, request)
  }

  const updateReference = async (
    mBoxId: number,
    request: Reference,
  ): Promise<HttpResponse<Shipment | null, MotionBoxErrorCode>> => {
    return await motionViewApi.updateReference(mBoxId, request)
  }

  const uploadFile = async (
    mBoxId: number,
    file: File,
  ): Promise<HttpResponse<Attachment, MotionBoxErrorCode>> => {
    const formData = new FormData()
    formData.append('file', file)

    return await motionViewApi.uploadFile(mBoxId, formData)
  }

  const createNotification = async (
    mBoxId: number,
    request: NotificationRequest,
  ): Promise<HttpResponse<Shipment | null, MotionBoxErrorCode>> => {
    return await motionViewApi.createNotification(mBoxId, request)
  }

  const getMotionBoxType = (mType: string) => {
    switch (mType) {
      case 'SH':
        return MotionBoxType.Shipment
      case 'CO':
        return MotionBoxType.CustomerOrder
      case 'WO':
        return MotionBoxType.WarehouseOrder
      default:
        return MotionBoxType.None
    }
  }

  const getIndexOfItem = (id: number): MotionTrack => {
    const index = items.value.findIndex((item: MBox) => item.id === id)
    if (index === -1) return MotionTrackDefault
    return {
      id,
      index,
      status: items.value[index].status,
      mType: getMotionBoxType(items.value[index].mType),
    }
  }

  const getPreviousRelativeTo = (id: number | null): MotionTrack => {
    if (id === null) return MotionTrackDefault
    const index = items.value.findIndex((item: MBox) => item.id === id)
    if (index == 0) return MotionTrackDefault
    return {
      id: items.value[index - 1].id,
      index,
      status: items.value[index - 1].status,
      mType: getMotionBoxType(items.value[index].mType),
    }
  }

  const getNextRelativeTo = (id: number | null): MotionTrack => {
    if (id === null) return MotionTrackDefault
    const index = items.value.findIndex((item: MBox) => item.id === id) + 1
    if (index >= items.value.length) return MotionTrackDefault

    return {
      id: items.value[index].id,
      index,
      status: items.value[index].status,
      mType: getMotionBoxType(items.value[index].mType),
    }
  }

  const getNextFirstViewDateRelativeTo = (id: number | null): MotionTrack => {
    if (id === null) return MotionTrackDefault
    const baseIndex = items.value.findIndex((item: MBox) => item.id === id)
    let i = baseIndex
    if (items.value[i].viewDate && i < items.value.length - 1) i++ // were already at first item on day
    while (!items.value[i].viewDate) {
      i++
      if (i >= items.value.length) {
        return {
          id: items.value[baseIndex].id,
          index: baseIndex,
          status: items.value[baseIndex].status,
          mType: getMotionBoxType(items.value[baseIndex].mType),
        }
      }
    }
    return {
      id: items.value[i].id,
      index: i,
      status: items.value[i].status,
      mType: getMotionBoxType(items.value[i].mType),
    }
  }

  const getPrevFirstViewDateRelativeTo = (id: number | null): MotionTrack => {
    if (id === null) return MotionTrackDefault
    const baseIndex = items.value.findIndex((item: MBox) => item.id === id)
    let i = baseIndex
    if (items.value[i].viewDate && i > 0) i-- // were already at first item on day
    while (!items.value[i].viewDate) {
      i--
      if (i < 0)
        return {
          id: items.value[baseIndex].id,
          index: baseIndex,
          status: items.value[i].status,
          mType: getMotionBoxType(items.value[i].mType),
        }
    }
    return {
      id: items.value[i].id,
      index: i,
      status: items.value[i].status,
      mType: getMotionBoxType(items.value[i].mType),
    }
  }

  const getIdsInRange = (fromId: number, toId: number): MotionTrack[] => {
    const range: MotionTrack[] = []
    let iStart = items.value.findIndex((item: MBox) => item.id === fromId)
    let iEnd = items.value.findIndex((item: MBox) => item.id === toId)
    if (iEnd < iStart) {
      const tmp = iEnd
      iEnd = iStart
      iStart = tmp
    }
    for (let i = iStart; i <= iEnd; i++) {
      const item = items.value[i]
      range.push({
        id: item.id,
        index: i,
        status: item.status,
        mType: getMotionBoxType(items.value[i].mType),
      })
    }
    return range
  }

  const getAllIds = (): MotionTrack[] => {
    const mapped: MotionTrack[] = []
    items.value.forEach((item, index) =>
      mapped.push({
        id: item.id,
        index,
        status: item.status,
        mType: getMotionBoxType(item.mType),
      }),
    )
    return mapped
  }

  const resetAll = () => {
    lastKnownChangeVersion.value = '0'
    trackId.value = 0
    items.value = []
    priorityItems.value = []
  }

  return {
    items,
    priorityItems,
    isLoading,
    statuses,
    currentIndex,
    currentMotionBoxId,
    currentMotionBoxType,
    currentDimension,
    currentDimensionHistory,
    currentGeneralInfo,
    currentGeneralHistory,
    currentAdditionalInfo,
    currentSendTo,
    currentSendFrom,
    currentCarrier,
    currentCarrierHistory,
    currentCarrierSetting,
    currentOrderInfo,
    currentOrderDelivery,
    currentOrderDeliveryHistory,
    currentShipmentInfo,
    currentShipmentDelivery,
    currentShipmentDeliveryHistory,
    currentShipmentSpecification,
    currentRecipient,
    currentPayer,
    hasGeneralInfo,
    hasGeneralHistory,
    hasAdditionalInfo,
    hasSendTo,
    hasSendFrom,
    hasDimensions,
    hasDimensionHistory,
    hasCarrier,
    hasCarrierHistory,
    hasCarrierSetting,
    hasOrderInfo,
    hasOrderDelivery,
    hasOrderDeliveryHistory,
    hasShipmentInfo,
    hasShipmentDelivery,
    hasShipmentDeliveryHistory,
    hasShipmentSpecification,
    hasRecipient,
    hasPayer,
    setCurrentBox,
    clearCurrentBox,
    loadStatuses,
    loadGeneralInfo,
    loadAdditionalInfo,
    loadDimension,
    loadDimensionHistory,
    loadSendTo,
    loadSendFrom,
    loadGeneralHistory,
    loadCarrier,
    loadCarrierHistory,
    loadCarrierSetting,
    loadOrderInfo,
    loadOrderDelivery,
    loadOrderDeliveryHistory,
    loadShipmentInfo,
    loadShipmentDelivery,
    loadShipmentDeliveryHistory,
    loadShipmentSpecification,
    loadRecipient,
    loadPayer,
    getDimensionValueIds,
    getShipmentLabelByRelatedId,
    getShipmentDate,
    getReference,
    getExternalKey,
    getAdminExternalKey,
    getDeliveryNote,
    getAdminDeliveryNote,
    getStatusValidation,
    refreshMotionItems,
    updateMotionItems,
    updateStatus,
    updateDimensions,
    updateShipmentDate,
    updateSingle,
    updateReference,
    uploadFile,
    createNotification,
    resetAll,
    getIndexOfItem,
    getMotionSelectedItems,
    firstItem,
    getPreviousRelativeTo,
    getNextRelativeTo,
    getNextFirstViewDateRelativeTo,
    getPrevFirstViewDateRelativeTo,
    getAllIds,
    getIdsInRange,
  }
})
